/**
 * ARREGLAR DESDE EL AVISO.
 *
 * El diagnostico ya calcula la medida que falta: dice «39 ppp, exige 150» o «el
 * trazo mide 0,3 mm y el minimo son 0,5». Ofrecer la correccion es aprovechar
 * un trabajo YA HECHO, y convierte un muro en un paso.
 *
 * Lo que se devuelve es un PARCHE de la capa, no una accion: asi el arreglo se
 * puede probar comparando documentos, entra en el historial como cualquier otro
 * cambio y se deshace con Ctrl+Z. Un arreglo que no se pudiera deshacer daria
 * mas miedo que el propio aviso.
 *
 * Ningun arreglo INVENTA nada: todos usan la cifra que el propio diagnostico
 * declara como esperada. Si un dia esa cifra cambiara, el arreglo cambia con
 * ella sin tocar este fichero.
 */
import { escalaParaCumplirDpi, layerBBoxMm, type Layer } from '@besbello/design-core';
import type { Diagnostic } from '@besbello/rules-core';
import { limitesDe, meterEnZona, type Limites } from './encaje.js';

export interface Arreglo {
  /** Clave de i18n del boton. */
  clave: string;
  /** Numero que se mete en el texto, ya redondeado. */
  valor?: number;
  /** El cambio que hay que aplicarle a la capa. */
  parche: Partial<Layer>;
}

/** Escala una capa por un factor manteniendo su esquina superior izquierda. */
function escalar(capa: Layer, factor: number): Partial<Layer> {
  const t = capa.transform;
  return {
    transform: { ...t, a: t.a * factor, b: t.b * factor, c: t.c * factor, d: t.d * factor },
  } as Partial<Layer>;
}

/**
 * Que se puede hacer con este aviso, si es que se puede hacer algo.
 *
 * Devuelve `null` cuando el arreglo no es automatizable. Son casos reales y no
 * una carencia: «demasiadas tintas» se arregla decidiendo QUE color quitar, y
 * eso no lo puede elegir el programa por el cliente.
 */
export function arregloPara(
  diagnostico: Diagnostic,
  capa: Layer,
  zonaPolygonMm: Array<{ x: number; y: number }>,
): Arreglo | null {
  switch (diagnostico.code) {
    case 'EFFECTIVE_DPI_TOO_LOW': {
      if (capa.kind !== 'image' || diagnostico.expected === undefined) return null;
      const factor = escalaParaCumplirDpi(capa, diagnostico.expected);
      if (factor >= 1) return null;
      const anchoActual = Math.hypot(capa.transform.a, capa.transform.b);
      return {
        clave: 'fix.resize',
        valor: Number((anchoActual * factor).toFixed(1)),
        parche: escalar(capa, factor),
      };
    }

    case 'LAYER_OUTSIDE_ZONE': {
      const zona: Limites = limitesDe(zonaPolygonMm);
      const b = layerBBoxMm(capa);
      const caja = { x: b.minX, y: b.minY, anchoMm: b.maxX - b.minX, altoMm: b.maxY - b.minY };
      const dentro = meterEnZona(caja, zona);
      if (Math.abs(dentro.x - caja.x) < 0.01 && Math.abs(dentro.y - caja.y) < 0.01) return null;
      return {
        clave: 'fix.moveInside',
        parche: {
          transform: {
            ...capa.transform,
            e: capa.transform.e + (dentro.x - caja.x),
            f: capa.transform.f + (dentro.y - caja.y),
          },
        } as Partial<Layer>,
      };
    }

    case 'STROKE_TOO_THIN': {
      if (diagnostico.expected === undefined) return null;
      /*
        El trazo se engorda al MINIMO EXACTO de la tecnica, no un poco mas.
        Redondear hacia arriba «por si acaso» cambiaria el diseno mas de lo que
        el aviso pide, y en un contorno de texto eso se nota.
      */
      const escala = Math.hypot(capa.transform.a, capa.transform.b) || 1;
      const grosorNecesario = diagnostico.expected / escala;

      if (capa.kind === 'shape') {
        return {
          clave: 'fix.thickenStroke',
          valor: Number(diagnostico.expected.toFixed(2)),
          parche: { strokeWidthMm: grosorNecesario } as Partial<Layer>,
        };
      }
      if (capa.kind === 'text' && capa.outline) {
        return {
          clave: 'fix.thickenStroke',
          valor: Number(diagnostico.expected.toFixed(2)),
          parche: { outline: { ...capa.outline, widthMm: grosorNecesario } } as Partial<Layer>,
        };
      }
      return null;
    }

    case 'TEXT_TOO_SMALL': {
      if (capa.kind !== 'text' || diagnostico.expected === undefined) return null;
      const escala = Math.min(Math.abs(capa.transform.a), Math.abs(capa.transform.d)) || 1;
      return {
        clave: 'fix.growText',
        valor: Number(diagnostico.expected.toFixed(1)),
        parche: {
          sizeMm: diagnostico.expected / escala,
          // El interlineado sigue al cuerpo: dejarlo quieto amontonaria las
          // lineas de un texto que acaba de crecer.
          lineHeightMm: (diagnostico.expected / escala) * (capa.lineHeightMm / capa.sizeMm),
        } as Partial<Layer>,
      };
    }

    case 'ARC_OVERLAP': {
      if (capa.kind !== 'text' || !capa.arc) return null;
      /*
        El radio se sube al que deja el texto justo en una vuelta, mas un diez
        por ciento de holgura: dejarlo EXACTO en una vuelta pega la primera
        letra con la ultima, que se lee tan mal como si se pisaran.
      */
      const anchoAproximadoMm = capa.sizeMm * 0.55 * [...capa.text].length;
      const radioMinimo = (anchoAproximadoMm / (2 * Math.PI)) * 1.1;
      return {
        clave: 'fix.widenArc',
        valor: Number(radioMinimo.toFixed(1)),
        parche: { arc: { ...capa.arc, radiusMm: radioMinimo } } as Partial<Layer>,
      };
    }

    default:
      // TOO_MANY_INKS, GRADIENT_NOT_ALLOWED, SUBSTRATE_NOT_SUPPORTED y los
      // avisos informativos no se arreglan solos: exigen una DECISION del
      // cliente sobre que quitar o que cambiar, y elegir por el seria peor.
      return null;
  }
}
